import { Button, Container, PopupModal, Spinner, TopBar } from 'components'
import {
    MobileMoneyAmount,
    MobileMoneySelection,
    PaymentFeedbackMessage
} from 'components/enrollForm'
import {
    buildClientUrl,
    constants,
    getCountryInitials,
    getStorage,
    paymentStartEvent,
    paymentWaitExitEvent,
    shouldOpenPaymentRedirect
} from 'helpers'
import {
    useBindDispatch,
    useAppSelector,
    usePaymentPolling,
    useAnalytics
} from 'hooks'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './mobileMoney.scss'

const AMOUNT_PAGE = 'amountPage'
const MOBILE_MONEY_SELECTION_PAGE = 'mobileMoneySelectionPage'

export const MobileMoney = () => {
    const localClient = getStorage('client_details') || {
        village_id: '',
        client_details_type: ''
    }
    const { trackEvent, trackTimedEvent, resetTimedEvent } = useAnalytics()

    const [searchParams] = useSearchParams()
    const [selectPage, setSelectPage] = useState(AMOUNT_PAGE)
    const [mobileMoneyAmount, setMobileMoneyAmount] = useState(0)
    const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(false)
    const [showMobilePaymentSpinner, setShowMobilePaymentSpinner] =
        useState(false)
    const [providerPromptOpen, setProviderPromptOpen] = useState(false)
    const paymentSkippedRef = useRef(false)
    const paymentStartTrackedRef = useRef(false)
    const paymentAttemptActiveRef = useRef(false)
    const paymentOutcomeTrackedRef = useRef(false)
    const clientCode = searchParams.get('client-code')
    const fromUrl = searchParams.get('from')
    const navigate = useNavigate()
    const { t } = useTranslation()

    const endPollingPaymentCallback = () => {
        if (paymentSkippedRef.current) return
        setShowMobilePaymentSpinner(false)
        setShowMobilePaymentModal(true)
    }

    const { startPollingPayment, stopPollingPayment } = usePaymentPolling(
        endPollingPaymentCallback
    )
    const { requestMobilePaymentHandler, clearMobilePaymentHandler } =
        useBindDispatch()
    const {
        loading: mobilePaymentLoading,
        response: mobilePaymentResponse,
        error: mobilePaymentError,
        status: mobilePaymentStatus,
        uuid: mobilePaymentUuid
    } = useAppSelector((store) => store.mobilePayment)
    const { country } = getStorage(constants.CONNECT_USER)

    const village =
        searchParams.get('village-id') ||
        localClient?.village_id ||
        localClient?.village

    // `payment start` opens a Matomo payment attempt and is the denominator for
    // the payment rates. It fires on arrival (the funnel entry, before a
    // provider is chosen) and again on a request made after an outcome closed
    // the previous attempt, so every success / failure pairs with one start.
    const trackPaymentStart = () => {
        if (paymentStartTrackedRef.current) return
        paymentStartTrackedRef.current = true
        trackEvent(paymentStartEvent())
    }

    // Status / request-error Matomo only after requestMobilePayment
    // (paymentAttemptActiveRef) so leftover Redux from a prior page cannot emit
    // a phantom outcome on mount. One outcome per attempt, and tracking it
    // closes the attempt.
    const trackPaymentOutcome = (name: string) => {
        if (
            !paymentAttemptActiveRef.current ||
            paymentOutcomeTrackedRef.current
        ) {
            return
        }
        paymentOutcomeTrackedRef.current = true
        paymentStartTrackedRef.current = false
        trackTimedEvent({
            category: constants.ANALYTICS.CATEGORIES.PAYMENT,
            action: constants.ANALYTICS.ACTIONS.MOBILE_MONEY_PAYMENT,
            name
        })
    }

    const paymentStatusIsFinal =
        mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS ||
        mobilePaymentStatus === constants.MOBILE_MONEY.FAILURE

    useEffect(() => {
        // A terminal status left in the store sends this mount straight to
        // the client page (effect below), so no attempt opens for it.
        if (paymentStatusIsFinal) return
        trackPaymentStart()
    }, [])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (paymentStatusIsFinal) {
            // The client page only self-tracks the provider redirect, which
            // arrives with no router state. In-app navigation always marks the
            // outcome as handled: either this page tracked it, or the status is
            // left over from a prior attempt and is not an outcome at all.
            trackPaymentOutcome(
                mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS
                    ? constants.ANALYTICS.NAMES.PAYMENT_SUCCESS
                    : constants.ANALYTICS.NAMES.PAYMENT_FAILURE
            )
            navigate(
                `${buildClientUrl({
                    ...localClient,
                    client_code: clientCode,
                    village_id: village
                })}?mobile-payment=${mobilePaymentStatus}&mm-amount=${mobileMoneyAmount}`,
                { replace: true, state: { mobilePaymentTracked: true } }
            )
        }
    }, [mobilePaymentStatus])

    useEffect(() => {
        if (
            !shouldOpenPaymentRedirect(
                mobilePaymentResponse,
                mobilePaymentUuid,
                paymentSkippedRef.current
            )
        ) {
            return
        }
        if (mobilePaymentUuid) {
            startPollingPayment(mobilePaymentUuid)
        }
        window.open(
            mobilePaymentResponse?.redirect_url,
            '_blank',
            'noopener,noreferrer'
        )
    }, [mobilePaymentResponse, mobilePaymentUuid])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (mobilePaymentError) {
            setShowMobilePaymentModal(true)
            setShowMobilePaymentSpinner(false)
            trackPaymentOutcome(constants.ANALYTICS.NAMES.PAYMENT_FAILURE)
            resetTimedEvent()
        }
    }, [mobilePaymentError])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (mobilePaymentLoading) {
            setShowMobilePaymentSpinner(true)
        }
    }, [mobilePaymentLoading])

    const navigateAwayFromPayment = (fallback: string | number = '/') => {
        if (fromUrl) {
            return navigate(fromUrl, { replace: true })
        }
        if (
            village &&
            localClient?.client_details_type !==
                constants.FORM_TYPES.RE_ENROLLMENT
        ) {
            return navigate(
                buildClientUrl({
                    ...localClient,
                    client_code: clientCode,
                    village_id: village
                }),
                { replace: true }
            )
        }
        if (!village) {
            navigate('/')
            return
        }
        if (typeof fallback === 'number') {
            navigate(fallback)
            return
        }
        navigate(fallback)
    }

    const leavePaymentPage = (
        exitName: string,
        fallback: string | number = '/'
    ) => {
        trackTimedEvent({
            category: constants.ANALYTICS.CATEGORIES.PAYMENT,
            action: constants.ANALYTICS.ACTIONS.MOBILE_MONEY_PAYMENT,
            name: exitName
        })
        navigateAwayFromPayment(fallback)
    }

    const skipPaymentHandler = () => {
        if (mobilePaymentResponse?.redirect_url) {
            setProviderPromptOpen(true)
            setShowMobilePaymentSpinner(false)
            setShowMobilePaymentModal(true)
            return
        }
        paymentSkippedRef.current = true
        stopPollingPayment()
        setShowMobilePaymentSpinner(false)
        setShowMobilePaymentModal(false)
        clearMobilePaymentHandler()
        leavePaymentPage(paymentWaitExitEvent().name)
    }

    const closeCardSaleHandler = (route: string | number) => {
        leavePaymentPage(
            `${constants.ANALYTICS.NAMES.PAYMENT_EXITED}-${selectPage}`,
            route
        )
    }

    const connectBaseUrl = window.location.origin

    const requestMobilePayment = (selectedMobileMoneyProvider: string) => {
        paymentSkippedRef.current = false
        setProviderPromptOpen(false)
        paymentAttemptActiveRef.current = true
        paymentOutcomeTrackedRef.current = false
        trackPaymentStart()
        resetTimedEvent()
        requestMobilePaymentHandler({
            country_code: getCountryInitials(country),
            client_code: clientCode || '',
            provider: selectedMobileMoneyProvider,
            amount: mobileMoneyAmount,
            success_url: `${connectBaseUrl}/village/${village}/client/${clientCode}?mobile-payment=success&mm-amount=${mobileMoneyAmount}`,
            error_url: `${connectBaseUrl}/village/${village}/client/${clientCode}?mobile-payment=failure&mm-amount=${mobileMoneyAmount}`
        })
    }

    const mobileMoneyAmountComponent = () => (
        <MobileMoneyAmount
            continueHandler={(amount) => {
                setMobileMoneyAmount(amount)
                setSelectPage(MOBILE_MONEY_SELECTION_PAGE)
            }}
            t={t}
            initialValue={mobileMoneyAmount}
        />
    )

    const mobileMoneySelectionComponent = () => (
        <MobileMoneySelection t={t} mobileMoneyHandler={requestMobilePayment} />
    )

    const pageConfig = {
        amountPage: mobileMoneyAmountComponent,
        mobileMoneySelectionPage: mobileMoneySelectionComponent
    }

    const closeMobileMoneyModal = () => {
        stopPollingPayment()
        clearMobilePaymentHandler()
        setShowMobilePaymentModal(false)
    }

    return (
        <>
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar
                        back
                        backNavigation={() => closeCardSaleHandler(-1)}
                        close={() => closeCardSaleHandler('/')}
                    >
                        {t('mobileMoney.mobileMoneyPayment')}
                    </TopBar>
                </Container>
            </div>
            <Container sm={12}>
                {pageConfig[selectPage as keyof typeof pageConfig]?.()}
            </Container>
            {showMobilePaymentSpinner && (
                <div
                    className="mobile-money--loading-wrapper"
                    aria-label="mobile-money-loading"
                >
                    <Spinner fullscreen size="100" />
                    <div className="payment-wait__skip">
                        <Button
                            style="outline"
                            type="button"
                            onClick={skipPaymentHandler}
                            id="skip-payment"
                        >
                            {t('mobileMoney.skipPayment')}
                        </Button>
                    </div>
                </div>
            )}
            {showMobilePaymentModal && (
                <PopupModal closeModal={closeMobileMoneyModal}>
                    <PaymentFeedbackMessage
                        status={
                            mobilePaymentStatus ||
                            (providerPromptOpen
                                ? constants.MOBILE_MONEY.PENDING
                                : constants.MOBILE_MONEY.FAILURE)
                        }
                        amount={mobileMoneyAmount}
                        t={t}
                        showRetryButton={false}
                        continueHandler={() => {
                            closeMobileMoneyModal()
                            if (
                                providerPromptOpen &&
                                mobilePaymentStatus !==
                                    constants.MOBILE_MONEY.SUCCESS &&
                                mobilePaymentStatus !==
                                    constants.MOBILE_MONEY.FAILURE
                            ) {
                                navigateAwayFromPayment()
                                return
                            }
                            navigate(
                                buildClientUrl({
                                    ...localClient,
                                    client_code: clientCode,
                                    village_id: village
                                }),
                                {
                                    replace: true
                                }
                            )
                        }}
                    />
                </PopupModal>
            )}
        </>
    )
}
